import {
  Box,
  ButtonBase,
  Divider,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { getVariantAttributeLabel } from "../../reduxStore/selectors/cartSelectors";
import { removeItemNote } from "../../reduxStore/slices/cartSlice";
import { formatCurrency } from "../../utils/currency";
import Iconify from "../iconify/Iconify";
import AddonSummary from "./AddonSummary";
import "./OrderMenuItem.css";

function OrderMenuItem({
  item,
  isEdit = false,
  onUpdate,
  onRemove,
  onNoteClick,
  lastAddedItemId,
  index,
  kotId,
  onVariantEdit = () => {},
}) {
  const dispatch = useDispatch();
  const [lastAddedId, setLastAddedId] = useState(null);
  const [highlightKey, setHighlightKey] = useState(0);
  const [qty, setQty] = useState(item.quantity || 1);
  const [note, setNote] = useState(item.note || "");
  const itemPrice = item?.current_price?.final_price || 0;
  const hasAttributes = item.attributes && item.attributes.length > 0;
  const addons = item.selectedAddons || [];

  useEffect(() => {
    setQty(item.quantity || 1);
  }, [item.quantity]);

  useEffect(() => {
    setNote(item.note || "");
  }, [item.note]);

  useEffect(() => {
    if (!lastAddedItemId) return;
    if (lastAddedItemId.id !== item.variant_uid) return;

    setLastAddedId(null);

    requestAnimationFrame(() => {
      setLastAddedId(item.variant_uid);
      setHighlightKey((prev) => prev + 1);
    });

    let attempts = 0;
    const maxAttempts = 10;

    const tryScroll = () => {
      const element = document.getElementById(`cart-item-${item.variant_uid}-${index}-${kotId}`);

      if (element) {
        element.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
        });
        return;
      }

      if (attempts < maxAttempts) {
        attempts++;
        setTimeout(tryScroll, 30);
      }
    };

    tryScroll();

    const timer = setTimeout(() => setLastAddedId(null), 1800);
    return () => clearTimeout(timer);
  }, [lastAddedItemId, item.variant_uid]);

  const handleQtyChange = (e) => {
    const val = parseInt(e.target.value, 10);
    if (!isNaN(val) && val >= 0) {
      setQty(val);
      onUpdate?.({ ...item, quantity: val, note });
    }
  };

  const attrName = getVariantAttributeLabel(item.attributes);

  return (
    <>
      <Box
        key={highlightKey}
        id={`cart-item-${item.variant_uid}-${index}-${kotId}`}
        className={`${item.variant_uid === lastAddedId ? "cart-item-new" : ""}`}
      >
        <Box className="menu-items-item">
          <Box className="menu-item-item-name menu-item-items">
            {!isEdit && (
              <IconButton onClick={() => onRemove?.(item)}>
                <Iconify icon={"eva:trash-2-outline"} />
              </IconButton>
            )}

            <Box
              sx={{
                flex: 1,
              }}
            >
              <Box>
                <Typography
                  onClick={() => {
                    if (!hasAttributes) return;
                    onVariantEdit(item);
                  }}
                  style={{
                    fontWeight: 600,
                    fontSize: 13,
                    color: hasAttributes ? "#1976d2" : "inherit",
                    cursor: hasAttributes ? "pointer" : "inherit",
                  }}
                >
                  {item.variant_name || "Unnamed Item"}
                </Typography>
                {attrName && (
                  <Typography
                    sx={{
                      fontSize: "0.75rem",
                      color: "text.secondary",
                    }}
                  >
                    {attrName}
                  </Typography>
                )}
              </Box>
              {!isEdit && note && (
                <Box
                  sx={{
                    display: "flex",
                    gap: "2px",
                    alignItems: "center",
                  }}
                >
                  <Box
                    style={{
                      fontSize: 11,
                      color: "#6c757d",
                    }}
                  >
                    {note}
                  </Box>
                  <Tooltip title="Remove">
                    <IconButton
                      size="small"
                      onClick={() =>
                        dispatch(
                          removeItemNote({
                            variant_uid: item.variant_uid,
                            note: "",
                          })
                        )
                      }
                    >
                      <Iconify icon="mdi:close" width={14} />
                    </IconButton>
                  </Tooltip>
                </Box>
              )}
            </Box>

            {!isEdit && note && (
              <Tooltip title="Edit instructions">
                <IconButton
                  onClick={() => onNoteClick(item)}
                  sx={{ color: "#3f51b5" }}
                >
                  <Iconify width={18} icon={"hugeicons:note-edit"} />
                </IconButton>
              </Tooltip>
            )}
            {!isEdit && !note && (
              <Tooltip title="Add instructions">
                <IconButton onClick={() => onNoteClick(item)}>
                  <Iconify width={18} icon={"hugeicons:note-add"} />
                </IconButton>
              </Tooltip>
            )}
          </Box>

          {/* Middle - Quantity */}
          <Box className="menu-item-input-content menu-item-qty">
            {!isEdit && (
              <ButtonBase
                className="menu-item-inc-btn"
                onClick={() => handleQtyChange({ target: { value: qty - 1 } })}
              >
                -
              </ButtonBase>
            )}

            <Box className="menu-item-input-wrap">
              <input
                type="number"
                value={qty}
                readOnly={isEdit}
                onChange={handleQtyChange}
                className="menu-item-input"
                name="qty_item"
                style={{
                  border: isEdit ? 0 : "1px solid #000",
                  textAlign: "center",
                }}
              />
            </Box>

            {!isEdit && (
              <ButtonBase
                className="menu-item-inc-btn"
                onClick={() => handleQtyChange({ target: { value: qty + 1 } })}
              >
                +
              </ButtonBase>
            )}
          </Box>

          <Box className="menu-item-price menu-item-price-cell">
            <Typography
              sx={{
                fontSize: "11px",
              }}
            >
              {formatCurrency(itemPrice)} × {qty} ={" "}
            </Typography>
            <strong>{formatCurrency(itemPrice * qty)}</strong>
          </Box>
        </Box>
        <AddonSummary addons={addons} itemPrice={itemPrice} itemQty={qty} />

        <Divider />
      </Box>
    </>
  );
}

export default OrderMenuItem;
